"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type FC,
  type PropsWithChildren,
} from "react";

import { shallowEqual } from "react-redux";

import { type CustomLayerTargetType } from "@/providers/store/api/custom-layers/custom-layers.interface";
import { type SchoolTypeEnum } from "@/providers/store/api/reports/reports.interface";
import { useAppSelector } from "@/providers/store/hooks";

import { clearCustomLayerEditSession } from "../_utils/custom-layer-edit-session.util";

import { useBoundariesEditGeometryCache } from "./boundaries-edit-geometry-cache-context";

export type BoundariesEditKind = "customLayer" | "mapLevel" | null;

export interface BoundariesEditModeContextValue {
  isBoundariesEditMode: boolean;
  editingLayerId: string | null;
  editingLayerName: string | null;
  editingSchoolType: SchoolTypeEnum | null;
  editingKind: BoundariesEditKind;
  /** Target type of the edited custom layer; null when not editing a custom layer. */
  editingTargetType: CustomLayerTargetType | null;
  setBoundariesEditMode: (
    value: boolean,
    layerId?: string | null,
    layerName?: string | null,
    schoolType?: SchoolTypeEnum | null,
    kind?: BoundariesEditKind,
    targetType?: CustomLayerTargetType | null,
  ) => void;
}

const BoundariesEditModeContext = createContext<BoundariesEditModeContextValue | null>(null);

export function useBoundariesEditMode() {
  const context = useContext(BoundariesEditModeContext);
  if (!context) {
    throw new Error(
      "useBoundariesEditMode must be used inside <BoundariesEditModeProvider />",
    );
  }
  return context;
}

export const BoundariesEditModeProvider: FC<PropsWithChildren> = ({ children }) => {
  const districtId = useAppSelector((s) => s.district.selectedDistrict?.id ?? null, shallowEqual);
  const { abortAndClear } = useBoundariesEditGeometryCache();
  const [isBoundariesEditMode, setIsBoundariesEditMode] = useState(false);
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  const [editingLayerName, setEditingLayerName] = useState<string | null>(null);
  const [editingSchoolType, setEditingSchoolType] = useState<SchoolTypeEnum | null>(null);
  const [editingKind, setEditingKind] = useState<BoundariesEditKind>(null);
  const [editingTargetType, setEditingTargetType] = useState<CustomLayerTargetType | null>(null);

  const setBoundariesEditMode = useCallback(
    (
      enabled: boolean,
      layerId?: string | null,
      layerName?: string | null,
      schoolType?: SchoolTypeEnum | null,
      kind?: BoundariesEditKind,
      targetType?: CustomLayerTargetType | null,
    ) => {
      setIsBoundariesEditMode(enabled);
      setEditingLayerId(enabled && layerId != null ? layerId : null);
      setEditingLayerName(enabled && layerName != null ? layerName : null);
      setEditingSchoolType(enabled ? (schoolType ?? null) : null);
      // Default to "customLayer" when enabling without an explicit kind, for
      // backward compat with existing CustomLayer callers that predate this param.
      setEditingKind(enabled ? (kind ?? "customLayer") : null);
      setEditingTargetType(enabled ? (targetType ?? null) : null);

      if (!enabled) {
        abortAndClear();
        clearCustomLayerEditSession(districtId);
      }
    },
    [abortAndClear, districtId],
  );

  const value: BoundariesEditModeContextValue = {
    isBoundariesEditMode,
    editingLayerId,
    editingLayerName,
    editingSchoolType,
    editingKind,
    editingTargetType,
    setBoundariesEditMode,
  };

  return (
    <BoundariesEditModeContext.Provider value={value}>
      {children}
    </BoundariesEditModeContext.Provider>
  );
};
